// The databases a site holds (36.12) and the one every request addresses.
//
// The list comes from `/api/v1/databases`; the active id lives in the
// preference store and rides every request as the X-Tres-Database header.
// A switch invalidates the whole query cache: database changes are rare,
// and every page's data belongs to the database it was read from.

import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listDatabases, type DatabaseList } from '@/lib/api';
import {
  DATABASE_CHANGED_EVENT,
  getActiveDatabase,
  setActiveDatabase,
} from '@/lib/preferences';
import { useApiSession } from '@/lib/useApiSession';

export function useDatabases() {
  const { token, ready } = useApiSession();
  return useQuery<DatabaseList>({
    queryKey: ['databases', token ?? 'anon'],
    enabled: ready,
    staleTime: 30_000,
    queryFn: async () => {
      const r = await listDatabases(token);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
}

/**
 * The active database id (`null` = the site's default) and the switch.
 * Every component that calls this re-renders on a switch made anywhere.
 */
export function useActiveDatabase(): {
  active: string | null;
  switchTo: (id: string | null) => void;
} {
  const [active, setActive] = useState<string | null>(() => getActiveDatabase());

  useEffect(() => {
    const onChange = () => setActive(getActiveDatabase());
    window.addEventListener(DATABASE_CHANGED_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(DATABASE_CHANGED_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  const switchTo = useCallback(
    (id: string | null) => {
      setActiveDatabase(id);
      setActive(getActiveDatabase());
    },
    [],
  );

  return { active, switchTo };
}

/**
 * The one place a switch reaches the shared cache; mounted once, in the
 * header. Everything in the cache was read from the previous database, so a
 * switch resets it, a reset and not an invalidation: an invalidated page
 * paints the old database's rows while its refetch runs, and the Rules page
 * did exactly that on the twin (2026-10-12). Runs for a switch made here and
 * for one made in another tab, which only the storage event carries. The same
 * subscriber forgets a picked database the site no longer has, so a removed
 * database never leaves every request refused.
 */
export function useDatabaseSwitchEffects(): void {
  const queryClient = useQueryClient();
  const databases = useDatabases();
  const { active, switchTo } = useActiveDatabase();
  useOnDatabaseChange(
    useCallback(() => {
      void queryClient.resetQueries();
      // A remembered search belongs to the database it was made on; the
      // graph pickers are forgotten on a switch and these go with them.
      for (const key of ['entitySearch.entity', 'vizSearch.viz']) {
        try {
          localStorage.removeItem(key);
        } catch {
          // storage unavailable: nothing to forget
        }
      }
    }, [queryClient]),
  );
  const list = databases.data;
  useEffect(() => {
    if (list && active && effectiveDatabase(list, active) === null) switchTo(null);
  }, [list, active, switchTo]);
}

/**
 * Run `onChange` whenever the database every page reads changes, whether the
 * switch was made in this tab or another. For state a page keeps outside the
 * query cache — an answer on screen, a canvas, a list loaded by hand — which
 * the cache reset cannot reach.
 */
export function useOnDatabaseChange(onChange: () => void): void {
  useEffect(() => {
    let last = getActiveDatabase();
    const handle = () => {
      const now = getActiveDatabase();
      if (now === last) return;
      last = now;
      onChange();
    };
    window.addEventListener(DATABASE_CHANGED_EVENT, handle);
    window.addEventListener('storage', handle);
    return () => {
      window.removeEventListener(DATABASE_CHANGED_EVENT, handle);
      window.removeEventListener('storage', handle);
    };
  }, [onChange]);
}

/**
 * The database every request addresses, as the site lists it, and how many
 * the site holds; `null` until the list loads. The header, Database
 * Management and the index badge all name it from here.
 */
export function useCurrentDatabase(): {
  database: DatabaseList['databases'][number] | null;
  count: number;
} {
  const databases = useDatabases();
  const { active } = useActiveDatabase();
  const list = databases.data;
  if (!list) return { database: null, count: 0 };
  const id = effectiveDatabase(list, active) ?? list.default;
  return {
    database: list.databases.find((d) => d.id === id) ?? null,
    count: list.databases.length,
  };
}

/**
 * The id a request will carry for the database list given: the picked one
 * when the site still has it, else the default. A picked database that was
 * removed falls back to the default rather than failing every request.
 */
export function effectiveDatabase(
  list: DatabaseList | undefined,
  active: string | null,
): string | null {
  if (!list) return active;
  if (active && list.databases.some((d) => d.id === active)) return active;
  return null;
}
